import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import {
  Plus, Edit, Mail, Phone, KeyRound, ShieldCheck, Loader2,
  UserX, UserCheck, Users,
} from 'lucide-react';
import { getStaff, addStaff, updateStaff, resetStaffPassword } from '../services/adminService';
import { useAuth } from '../context/AuthContext';
import { useHotelFeatures } from '../context/HotelFeaturesContext';

const MAX_ADMIN = 2;
const MAX_STAFF = 5;

const ROLES = [
  { id: 3, name: 'Hotel Admin', color: 'bg-blue-600' },
  { id: 4, name: 'Hotel Staff', color: 'bg-slate-500' },
];
const getRoleMeta = (id) => ROLES.find(r => r.id === id) || { name: 'Unknown', color: 'bg-slate-500' };

const EMPTY_FORM = { fullName: '', email: '', phone: '', password: '', role: '4', canMakeReservation: true, canSellTickets: false };

const LimitBar = ({ used, max, label, color }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-slate-400 w-24 shrink-0">{label}</span>
    <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${used >= max ? 'bg-red-500' : color}`}
        style={{ width: `${Math.min((used / max) * 100, 100)}%` }}
      />
    </div>
    <span className={`text-xs font-semibold tabular-nums ${used >= max ? 'text-red-400' : 'text-slate-300'}`}>
      {used}/{max}
    </span>
  </div>
);

const Staff = () => {
  const { user } = useAuth();
  const { isTicketEnabled } = useHotelFeatures();
  const isHead = user?.role === 'HotelHead' || user?.role === 'Admin';
  const isHotelAdmin = user?.role === 'HotelAdmin';
  const canManage = isHead || isHotelAdmin;

  const [staff, setStaff]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  const [isFormOpen, setIsFormOpen]     = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [form, setForm]                 = useState(EMPTY_FORM);

  const [isResetOpen, setIsResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const { toast } = useToast();

  const load = useCallback(async () => {
    try { setStaff(await getStaff()); }
    catch { toast({ title: 'Failed to load staff', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const setField = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const openAdd = () => { setEditingStaff(null); setForm(EMPTY_FORM); setIsFormOpen(true); };
  const openEdit = (s) => {
    setEditingStaff(s);
    setForm({ fullName: s.fullName, email: s.email, phone: s.phone || '', password: '', role: String(s.roleId), canMakeReservation: s.canMakeReservation ?? true, canSellTickets: s.canSellTickets ?? false });
    setIsFormOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const isStaffRole = parseInt(form.role) === 4;
      if (editingStaff) {
        await updateStaff(editingStaff.id, {
          fullName: form.fullName, phone: form.phone || null,
          role: parseInt(form.role), isActive: editingStaff.isActive,
          payType: editingStaff.payType ?? 0,
          hourlyRate: editingStaff.hourlyRate ?? null,
          monthlyRate: editingStaff.monthlyRate ?? null,
          canMakeReservation: isStaffRole ? form.canMakeReservation : true,
          canSellTickets: isStaffRole ? form.canSellTickets : true,
        });
        toast({ title: 'Account updated' });
      } else {
        await addStaff({
          fullName: form.fullName, email: form.email, phone: form.phone || null,
          password: form.password, role: parseInt(form.role),
          canMakeReservation: isStaffRole ? form.canMakeReservation : true,
          canSellTickets: isStaffRole ? form.canSellTickets : true,
        });
        toast({ title: 'Account created' });
      }
      setIsFormOpen(false);
      await load();
    } catch (err) {
      toast({ title: err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (s) => {
    try {
      await updateStaff(s.id, { fullName: s.fullName, phone: s.phone, role: s.roleId, isActive: !s.isActive, payType: s.payType ?? 0, hourlyRate: s.hourlyRate ?? null, monthlyRate: s.monthlyRate ?? null, canMakeReservation: s.canMakeReservation ?? true, canSellTickets: s.canSellTickets ?? false });
      toast({ title: s.isActive ? 'Account deactivated' : 'Account activated' });
      await load();
    } catch (err) { toast({ title: err.message, variant: 'destructive' }); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await resetStaffPassword(resetTarget.id, newPassword);
      toast({ title: 'Password reset successfully' });
      setIsResetOpen(false); setNewPassword('');
    } catch (err) { toast({ title: err.message, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const admins = staff.filter(s => s.roleId === 3);
  const staffAcc = staff.filter(s => s.roleId === 4);
  const activeAdmins = admins.filter(s => s.isActive).length;
  const activeStaff  = staffAcc.filter(s => s.isActive).length;
  const canAddAdmin  = activeAdmins < MAX_ADMIN;
  const canAddStaff  = activeStaff  < MAX_STAFF;

  if (loading) return (
    <div className="space-y-6">
      <div><h1 className="text-3xl font-bold text-white">Portal Accounts</h1></div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {[1,2,3].map(i => <Card key={i} className="bg-slate-800/50 border-slate-700 animate-pulse"><CardContent className="p-6 h-36"/></Card>)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Portal Accounts</h1>
          <p className="text-slate-400 mt-1">Manage admin and staff login accounts</p>
        </div>
        {canManage && (
          <Button
            onClick={openAdd}
            disabled={isHotelAdmin ? !canAddStaff : (!canAddAdmin && !canAddStaff)}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            <Plus className="h-4 w-4 mr-2" />Add Account
          </Button>
        )}
      </div>

      {/* Limit summary */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-5 space-y-3">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Account Limits</p>
          <LimitBar used={activeAdmins} max={MAX_ADMIN} label="Hotel Admin" color="bg-blue-500" />
          <LimitBar used={activeStaff}  max={MAX_STAFF}  label="Hotel Staff" color="bg-slate-400" />
        </CardContent>
      </Card>

      {/* Staff cards */}
      {staff.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <Users className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No Accounts Yet</h3>
            <p className="text-slate-400 mb-4">Add your first portal account to get started.</p>
            {canManage && <Button onClick={openAdd} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-2" />Add Account</Button>}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {staff.map(s => {
            const role = getRoleMeta(s.roleId);
            return (
              <Card key={s.id} className={`border-slate-700 transition-all ${s.isActive ? 'bg-slate-800/50 hover:bg-slate-800/70' : 'bg-slate-800/20 opacity-60'}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${role.color}`}>
                        {(s.fullName || s.email).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm leading-tight">{s.fullName || '—'}</p>
                        <Badge className={`text-xs mt-0.5 ${role.color} text-white border-0`}>{role.name}</Badge>
                      </div>
                    </div>
                    {!s.isActive && <Badge className="text-xs bg-red-900/50 text-red-400 border border-red-700/50">Inactive</Badge>}
                  </div>

                  <div className="space-y-1.5 mb-4">
                    <div className="flex items-center gap-2 text-sm text-slate-400"><Mail className="h-3.5 w-3.5 flex-shrink-0" /><span className="truncate">{s.email}</span></div>
                    {s.phone && <div className="flex items-center gap-2 text-sm text-slate-400"><Phone className="h-3.5 w-3.5 flex-shrink-0" /><span>{s.phone}</span></div>}
                    <div className="text-xs text-slate-500">Joined {new Date(s.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                    {s.roleId === 4 && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${s.canMakeReservation ? 'bg-blue-500/15 text-blue-400 border-blue-500/30' : 'bg-slate-700/50 text-slate-500 border-slate-600/30 line-through'}`}>
                          Reservations
                        </span>
                        {isTicketEnabled && (
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${s.canSellTickets ? 'bg-purple-500/15 text-purple-400 border-purple-500/30' : 'bg-slate-700/50 text-slate-500 border-slate-600/30 line-through'}`}>
                            Tickets
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {(isHead || (isHotelAdmin && s.roleId === 4)) && (
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(s)} className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700 h-7 px-2.5 text-xs">
                        <Edit className="h-3 w-3 mr-1" />Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setResetTarget(s); setNewPassword(''); setIsResetOpen(true); }} className="border-amber-600/50 text-amber-400 hover:bg-amber-600/20 h-7 px-2.5 text-xs">
                        <KeyRound className="h-3 w-3 mr-1" />Password
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleToggleActive(s)}
                        className={`h-7 px-2.5 text-xs ${s.isActive ? 'border-red-600/50 text-red-400 hover:bg-red-600/20' : 'border-green-600/50 text-green-400 hover:bg-green-600/20'}`}>
                        {s.isActive ? <><UserX className="h-3 w-3 mr-1" />Deactivate</> : <><UserCheck className="h-3 w-3 mr-1" />Activate</>}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{editingStaff ? 'Edit Account' : 'Add Account'}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingStaff ? 'Update name, phone, or role.' : 'Create a new portal login account.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={form.fullName} onChange={setField('fullName')} className="bg-slate-700 border-slate-600" placeholder="e.g. Rafi Ahmed" required />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={setField('email')} className="bg-slate-700 border-slate-600"
                placeholder="staff@hotel.com" required disabled={!!editingStaff} />
              {editingStaff && <p className="text-xs text-slate-500">Email is the login ID and cannot be changed.</p>}
            </div>
            <div className="space-y-2">
              <Label>Phone <span className="text-slate-500 font-normal text-xs">(optional)</span></Label>
              <Input value={form.phone} onChange={setField('phone')} className="bg-slate-700 border-slate-600" placeholder="+880 1XXX-XXXXXX" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  {!isHotelAdmin && (
                    <SelectItem value="3" disabled={!editingStaff && !canAddAdmin}>Hotel Admin {!editingStaff && !canAddAdmin ? '(limit reached)' : `(${activeAdmins}/${MAX_ADMIN})`}</SelectItem>
                  )}
                  <SelectItem value="4" disabled={!editingStaff && !canAddStaff}>Hotel Staff {!editingStaff && !canAddStaff ? '(limit reached)' : `(${activeStaff}/${MAX_STAFF})`}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === '4' && (
              <div className="space-y-2">
                <Label>Permissions</Label>
                <div className="rounded-lg border border-slate-600 bg-slate-700/50 p-3 space-y-2.5">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.canMakeReservation}
                      onChange={e => setForm(p => ({ ...p, canMakeReservation: e.target.checked }))}
                      className="w-4 h-4 rounded border-slate-500 accent-blue-500"
                    />
                    <div>
                      <p className="text-sm text-white font-medium">Room Reservations</p>
                      <p className="text-xs text-slate-400">Access to Bookings, Admin Booking, Calendar</p>
                    </div>
                  </label>
                  {isTicketEnabled && (
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.canSellTickets}
                        onChange={e => setForm(p => ({ ...p, canSellTickets: e.target.checked }))}
                        className="w-4 h-4 rounded border-slate-500 accent-blue-500"
                      />
                      <div>
                        <p className="text-sm text-white font-medium">Ticket Sales</p>
                        <p className="text-xs text-slate-400">Access to Tickets — sell, verify &amp; confirm ticket payments</p>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            )}
            {!editingStaff && (
              <div className="space-y-2">
                <Label>Password</Label>
                <Input type="password" value={form.password} onChange={setField('password')} className="bg-slate-700 border-slate-600"
                  placeholder="Min 6 characters" required minLength={6} />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)} className="border-slate-600">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                {editingStaff ? 'Update' : 'Create Account'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetOpen} onOpenChange={setIsResetOpen}>
        <DialogContent className="max-w-sm bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription className="text-slate-400">Set a new password for <span className="text-white">{resetTarget?.fullName}</span></DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 pt-1">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                className="bg-slate-700 border-slate-600" placeholder="Min 6 characters" required minLength={6} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsResetOpen(false)} className="border-slate-600">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Reset Password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Staff;
